import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { AreaUnit, AreaValue } from '@/domain/shared/units';


const AreaUnitConst = {
  SQM: 'sqm',
  SQFT: 'sqft',
} as const;

type AreaUnitValue = (typeof AreaUnitConst)[keyof typeof AreaUnitConst];

function toNumberOrUndefined(v: string): number | undefined {
  if (v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function round2(n: number) {
  return Math.round(n * 100) / 100;
}

function toCanonicalSqm(value: number, unit: AreaUnit): number {
  // 1 sqft = 0.092903 sqm
  return unit === 'sqft' ? value * 0.092903 : value;
}

export function AreaValueField(props: {
  label?: string;
  value?: AreaValue;
  onChange: (next: AreaValue | undefined) => void;
}) {
  const { label = 'Area', value, onChange } = props;

  const enteredValue = value?.entered?.value;
  const enteredUnit = (value?.entered?.unit ?? 'sqft') as AreaUnitValue;

  const setEnteredValue = (v: string) => {
    const n = toNumberOrUndefined(v);
    if (n == null) {
      onChange(undefined);
      return;
    }

    const unit = (value?.entered?.unit ?? enteredUnit) as AreaUnit;
    onChange({
      canonical: toCanonicalSqm(n, unit),
      entered: { value: n, unit },
      v: 1,
    });
  };

  const setUnit = (unit: AreaUnitValue) => {
    const current = value?.entered?.value ?? 0;
    const nextCanonical = toCanonicalSqm(current, unit as AreaUnit);

    onChange({
      canonical: nextCanonical,
      entered: { value: current, unit: unit as AreaUnit },
      v: 1,
    });
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="grid gap-3 md:grid-cols-3">
        <Input
          type="number"
          placeholder="Not set"
          value={enteredValue ?? ''}
          onChange={(e) => setEnteredValue(e.target.value)}
        />

        <Select value={enteredUnit} onValueChange={(u) => setUnit(u as AreaUnitValue)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sqm">sqm</SelectItem>
            <SelectItem value="sqft">sqft</SelectItem>
          </SelectContent>
        </Select>

        <Input
          disabled
          value={value?.canonical != null ? `${round2(value.canonical)} sqm` : 'canonical (sqm)'}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Canonical value is always stored in sqm.
      </p>
    </div>
  );
}